import React, { useEffect, useRef, useCallback, useMemo, Suspense, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Box, useTexture, Stats, Environment, OrthographicCamera, PerspectiveCamera } from '@react-three/drei';
import { EffectComposer, Bloom, SSAO, DepthOfField, ToneMapping, Vignette, Noise } from '@react-three/postprocessing';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { EffectComposer as ThreeEffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { BlendFunction } from 'postprocessing';
import useSimulationStore from '../../store/simulationStore';
import { SimulationControls, ChemicalSpeciesDefinition, ReactionDefinition, Particle, ElementProperties, SpeciesGeometry, ReactionComponent } from '../../types/simulation';
import * as THREE from 'three';
import { shallow } from 'zustand/shallow';
import MoleculeGroup, { MoleculeGroupProps } from './MoleculeGroup';
import InstancedSpeciesMesh from './InstancedSpeciesMesh';
import ErrorBoundary from '../ErrorBoundary';
import { threeSceneManager } from '../../utils/ThreeSceneManager';

// Texture Imports
import floorTextureUrl from '../../assets/textures/blueprint-paper-texture.jpg';

// --- Define the expected geometry structure (matching InstancedSpeciesMesh) ---
interface AtomGeometry {
  element: string;
  position?: [number, number, number];
}
interface ProcessedSpeciesGeometry {
  atoms: AtomGeometry[];
}
// --- End Geometry Structure Definition ---

// Define the expected shape of the particle state array from the worker
type WorkerParticleState = {
  id: string;
  speciesName: string;
  isActive: boolean;
};

// Interface for the frame message data structure
interface FrameMessageData {
  type: 'frame';
  positions: ArrayBuffer;
  rotations: ArrayBuffer;
  colors: string[];
  activeFlags: ArrayBuffer;
  lastEventTypes: ('none' | 'reaction' | 'decay')[];
  particleCount: number;
}

// Extend FrameMessageData if particleUpdate isn't just sending position/rotation/color/active
// For now, we assume particleUpdate payload matches subset of Particle
interface ParticleUpdatePayload extends Partial<Omit<Particle, 'id' | 'position' | 'velocity'>> {
  id: string;
  speciesName?: string;
}

// Prop type for the state update callback
interface SceneContentProps {
  reactorSize: number;
  onStatesUpdate: (states: WorkerParticleState[]) => void; // Add callback prop
}

const highlightDurationMs = 500; // Highlight duration (0.5 seconds)

// Simple string hash function (can be defined outside component)
function simpleHash(str: string): number {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

const SceneContent: React.FC<SceneContentProps> = ({ reactorSize, onStatesUpdate }) => {
  // Fetch state needed for visuals/material & worker communication
  const {
    isRunning,
    metalness,
    roughness,
    emissiveIntensity,
    particleScale,
    // Fetch maps needed by InstancedSpeciesMesh
    elementProperties,
    speciesGeometryMap,
  } = useSimulationStore(
    (state) => ({
      isRunning: state.isRunning,
      metalness: state.metalness,
      roughness: state.roughness,
      emissiveIntensity: state.emissiveIntensity,
      particleScale: state.controls.particleScale,
      // Add maps
      elementProperties: state.elementProperties,
      speciesGeometryMap: state.speciesGeometry, // Assuming this holds ProcessedSpeciesGeometry
    }), shallow
  );
  const tilingFactor = useSimulationStore(state => state.controls.tilingFactor);
  const resetCount = useSimulationStore(state => state.resetCount);

  // Log the received speciesGeometryMap
  useEffect(() => {
  }, [speciesGeometryMap]);

  // Get full controls object ONLY for worker init payload
  const controlsForInit = useSimulationStore(state => state.controls);

  // --- Get R3F state OUTSIDE useFrame ---
  const { camera } = useThree();

  // --- Worker Setup ---
  const worker = useRef<Worker | null>(null);
  const workerIdRef = useRef<number>(0); // Track worker generation to ignore stale messages
  const positionsRef = useRef<Float32Array | null>(null);
  const rotationsRef = useRef<Float32Array | null>(null);
  const colorsRef = useRef<string[]>([]); // Store latest colors
  const activeFlagsRef = useRef<Uint8Array | null>(null);
  const highlightedParticlesRef = useRef<Map<number, number>>(new Map()); // Map<particleIndex, endTime>
  const particleOrderRef = useRef<string[]>([]); // Store ID order from worker
  const [particleCount, setParticleCount] = React.useState(0); // Visual count from worker
  const [isWorkerReady, setIsWorkerReady] = React.useState(false); // Worker status flag

  // --- State for rendered components --- (tiledParticlesData likely obsolete now)
  // const [tiledParticlesData, setTiledParticlesData] = useState<Omit<MoleculeGroupProps, 'key'>[]>([]);

  // NOTE: Particle transform data is NO LONGER stored in React state.
  // It now flows directly from worker → ThreeSceneManager → Three.js meshes
  // This prevents the memory leak from React keeping references to old Float32Arrays

  // --- Ref for Accumulated Rotation ---
  const accumulatedRotation = useRef(new THREE.Euler(0, 0, 0));
  const frameCounter = useRef(0); // <-- Define frame counter ref

  // --- Worker Initialization and Communication Effect ---
  useEffect(() => {
    setIsWorkerReady(false);

    // Increment worker ID to invalidate messages from old worker
    workerIdRef.current += 1;
    const currentWorkerId = workerIdRef.current;

    // Clear instance counts immediately to remove old particles from view
    useSimulationStore.setState({
      averageKEBySpecies: {},
      totalEnergy: 0,
    });

    // Terminate existing worker if any
    if (worker.current) {
      worker.current.terminate();
      worker.current = null;
    }

    try {
      worker.current = new Worker(new URL('../../workers/physics.worker.ts', import.meta.url));
    } catch (error) {
      console.error('[Reactor] Failed to create worker:', error);
      return;
    }

    // Add error handling for worker
    worker.current.onerror = (error) => {
      console.error('[Reactor] Worker runtime error:', error);
    };

    worker.current.onmessageerror = (error) => {
      console.error('[Reactor] Worker message error:', error);
    };

    // Message listener
    worker.current.onmessage = (event: MessageEvent<any>) => {
      // Ignore messages from old workers
      if (workerIdRef.current !== currentWorkerId) {
        return;
      }

      const { type, payload } = event.data;

      switch (type) {
        case 'initialized':
          // Clear any old instance data immediately
          useSimulationStore.setState({
            averageKEBySpecies: {},
            totalEnergy: 0,
            translationalEnergy: 0,
            rotationalEnergy: 0,
          });

          // Handle initial particle data
          if (payload && payload.particleRenderData) {
            const initialStates: WorkerParticleState[] = payload.particleRenderData.map((p: any) => ({
              id: p.id,
              speciesName: p.speciesName, // Assuming worker sends this now
              isActive: p.isActive,
            }));
            // Store initial particle order if needed, or just update counts
            setParticleCount(initialStates.filter(p => p.isActive).length);
            onStatesUpdate(initialStates); // Initial update to App
            frameCounter.current = 0;
            setIsWorkerReady(true);
          } else {
          }
          break;
        case 'physicsUpdate':
          // Check if payload has the expected structure (now expects particlesBySpecies)
          if (payload && payload.particlesBySpecies && payload.instanceCounts && payload.particleStates && payload.averageKEBySpecies) {
            // --- ROUTE 1: UI Data → React State (for graphs, displays) ---
            useSimulationStore.setState({
              averageKEBySpecies: payload.averageKEBySpecies,
              averageVelocitiesBySpecies: payload.averageVelocitiesBySpecies || {},
              // Energy tracking data
              totalEnergy: payload.totalEnergy || 0,
              translationalEnergy: payload.translationalEnergy || 0,
              rotationalEnergy: payload.rotationalEnergy || 0,
              initialTotalEnergy: payload.initialTotalEnergy || 0,
              energyRatio: payload.energyRatio || 1.0,
              thermostatEnabled: payload.thermostatEnabled || false
            });

            // Update particle states (for UI display of counts, etc.)
            const receivedStates = payload.particleStates as WorkerParticleState[];
            onStatesUpdate(receivedStates);

            // --- ROUTE 2: 60fps 3D Transform Data → ThreeSceneManager (bypasses React) ---
            threeSceneManager.updateAllParticles(payload.particlesBySpecies);

            frameCounter.current++;
            if (frameCounter.current % 300 === 0) {
              // console.log('[Reactor] 60fps data routed to ThreeSceneManager, UI data to React state');
            }
          } else {
            console.warn('[Reactor] Received malformed physicsUpdate payload', payload);
          }
          break;
        case 'error':
          // Maybe display error to user?
          break;
        default:
      }
    };

    // Error handler
    worker.current.onerror = (error) => {
    };

    // --- Prepare and Send initial data --- 
    const storeState = useSimulationStore.getState();

    // Use controls.reactions directly (legacy format) if activeChemistryConfig is empty
    const reactionsToUse = storeState.activeChemistryConfig.reactions.length > 0
      ? storeState.activeChemistryConfig.reactions
      : storeState.controls.reactions;

    // If using controls.reactions (legacy format), just pass them through
    if (storeState.activeChemistryConfig.reactions.length === 0) {
      // Legacy format - use controls.reactions directly
      const translatedReactions: ReactionDefinition[] = storeState.controls.reactions as any;

      const initialPayload = {
        gravity: { x: 0, y: 0, z: 0 },
        reactorSize: storeState.controls.reactorSize,
        wallThickness: 0.1,
        initialParticles: storeState.particles.map((p: Particle) => ({
          id: p.id,
          speciesName: p.particleType,
          isActive: p.isActive,
          position: p.position ?? [0, 0, 0],
        })),
        chemicalSpecies: storeState.controls.chemicalSpecies,
        reactions: translatedReactions,
        reactorWidth: storeState.controls.reactorWidth,
        reactorHeight: storeState.controls.reactorHeight,
        reactorDepth: storeState.controls.reactorDepth,
        particleSpeed: storeState.controls.particleSpeed,
        simulationSpeed: storeState.controls.simulationSpeed,
        collisionElasticity: storeState.controls.collisionElasticity,
        particleScale: storeState.controls.particleScale,
        tilingFactor: storeState.controls.tilingFactor,
        is2DMode: storeState.controls.is2DMode,
        speciesGeometry: storeState.speciesGeometry,
        elementProperties: storeState.elementProperties,
      };

      worker.current.postMessage({ type: 'init', payload: initialPayload as any });
      return;
    }

    // --- BEGIN Translation Logic (for activeChemistryConfig) --- 
    const { activeChemistryConfig, availableReactions, availableSpecies } = storeState;
    const speciesIdToNameMap = new Map<string, string>();
    availableSpecies.forEach(s => speciesIdToNameMap.set(s.id, s.name));

    // Debug logging disabled - uncomment if needed
    // console.log('[Reactor] Available species for mapping:', availableSpecies.map(s => ({ id: s.id, name: s.name })));
    // console.log('[Reactor] Active reactions to translate:', activeChemistryConfig.reactions);

    // Type the result of map explicitly to include null
    const mappedReactions: (ReactionDefinition | null)[] = activeChemistryConfig.reactions.map(activeReaction => {
      const baseReaction = availableReactions.find(r => r.id === activeReaction.reactionDefId);
      if (!baseReaction) {
        return null;
      }

      const mapCoeffToLegacy = (coeffs: { [speciesId: string]: number }): ReactionComponent[] => {
        return Object.entries(coeffs).map(([speciesId, count]) => {
          const name = speciesIdToNameMap.get(speciesId);
          if (!name) {
            console.warn('[Reactor] Could not find name for species ID:', speciesId);
            return { name: speciesId, count }; // Use ID as name fallback
          }
          return { name, count };
        }).filter(comp => comp.count > 0);
      };

      // Ensure name is always a string and probability/rateConstant have defaults if needed
      const translatedName = baseReaction.description || baseReaction.id;
      const translatedProbability = baseReaction.probability ?? 0; // Default probability to 0 if undefined
      const translatedRateConstant = (baseReaction as any).rateConstant; // Get rateConstant if it exists

      return {
        id: baseReaction.id,
        name: translatedName, // Ensure name is a string
        reactants: mapCoeffToLegacy(activeReaction.reactantsCoeff),
        products: mapCoeffToLegacy(activeReaction.productsCoeff),
        probability: translatedProbability, // Ensure probability is a number
        ...(translatedRateConstant !== undefined && { rateConstant: translatedRateConstant }) // Include rateConstant if present
      };
    });

    // Filter out nulls and assert the correct type
    const translatedReactions: ReactionDefinition[] = mappedReactions.filter((r): r is ReactionDefinition => r !== null);

    console.log('[Reactor] Translated reactions to send to worker:', translatedReactions.map(r => ({
      id: r.id,
      reactants: r.reactants,
      products: r.products,
      probability: r.probability,
      rateConstant: (r as any).rateConstant
    })));

    // --- END Translation Logic --- 

    const initialPayload = {
      gravity: { x: 0, y: 0, z: 0 }, // Assuming default gravity
      reactorSize: storeState.controls.reactorSize,
      wallThickness: 0.1, // Assuming default thickness
      // Send particles based on *current* store state (generated by resetSimulation)
      initialParticles: storeState.particles.map((p: Particle) => ({
        id: p.id,
        speciesName: p.particleType, // Worker expects speciesName
        isActive: p.isActive,
        position: p.position ?? [0, 0, 0],
      })),
      chemicalSpecies: storeState.controls.chemicalSpecies, // Send the recalculated legacy species list
      reactions: translatedReactions, // <-- Use translated reactions
      reactorWidth: storeState.controls.reactorWidth,
      reactorHeight: storeState.controls.reactorHeight,
      reactorDepth: storeState.controls.reactorDepth,
      particleSpeed: storeState.controls.particleSpeed,
      simulationSpeed: storeState.controls.simulationSpeed,
      collisionElasticity: storeState.controls.collisionElasticity,
      particleScale: storeState.controls.particleScale,
      tilingFactor: storeState.controls.tilingFactor,
      is2DMode: storeState.controls.is2DMode,
      speciesGeometry: storeState.speciesGeometry,
      elementProperties: storeState.elementProperties,
    };

    worker.current.postMessage({ type: 'init', payload: initialPayload as any });

    // Cleanup on unmount
    return () => {
      worker.current?.terminate();
      worker.current = null;
      setIsWorkerReady(false);
    };
    // Update dependencies: Include relevant parts of active config/definitions if translation needs to re-run
    // Using resetCount ensures re-init whenever simulation reset is triggered
    // Adding controlsForInit might be redundant if resetCount covers its changes, but safer?
  }, [resetCount, onStatesUpdate]); // Rely on resetCount to trigger re-initialization

  // --- Effect to send parameter updates to worker --- (Seems fine)
  useEffect(() => {
    if (!worker.current || !isWorkerReady) return;
    const paramUpdatePayload = {
      particleSpeed: controlsForInit.particleSpeed,
      simulationSpeed: controlsForInit.simulationSpeed,
      collisionElasticity: controlsForInit.collisionElasticity,
      particleScale: controlsForInit.particleScale,
      physicsQuality: controlsForInit.physicsQuality,
    };
    worker.current.postMessage({ type: 'setParam', payload: paramUpdatePayload });
  }, [controlsForInit.particleSpeed, controlsForInit.simulationSpeed, controlsForInit.collisionElasticity, controlsForInit.particleScale, controlsForInit.physicsQuality, isWorkerReady]);

  // --- Effect to send play/pause commands --- (Seems fine)
  useEffect(() => {
    if (!worker.current || !isWorkerReady) return;
    worker.current.postMessage({ type: isRunning ? 'start' : 'pause' });
  }, [isRunning, isWorkerReady]);

  // --- useFrame (Now empty as matrix calculations are moved) ---
  useFrame((state, delta) => {
    if (!isWorkerReady) return;
    // Can potentially be removed if nothing else needs frame updates here
  });

  // --- Add Logging before Render --- (Keep for debugging)
  if (frameCounter.current % 300 === 1) {
  }
  // --- End Logging ---

  return (
    <>
      {/* --- Render InstancedSpeciesMesh for each known species --- */}
      {/* Meshes are mounted once based on speciesGeometryMap, not on particle data */}
      {Object.keys(speciesGeometryMap).map(speciesName => {
        const speciesGeom = speciesGeometryMap[speciesName] as ProcessedSpeciesGeometry | undefined;

        if (!speciesGeom || !speciesGeom.atoms || speciesGeom.atoms.length === 0) {
          return null;
        }
        return (
          <ErrorBoundary key={`${speciesName}-boundary`} fallback={<div>Error rendering {speciesName}</div>}>
            <InstancedSpeciesMesh
              key={speciesName}
              speciesName={speciesName}
              speciesGeometry={speciesGeom}
              elementProperties={elementProperties}
              metalness={metalness}
              roughness={roughness}
              emissiveIntensity={emissiveIntensity}
              particleScale={particleScale}
            />
          </ErrorBoundary>
        );
      })}
    </>
  );
};


// --- SimulationView: Renders visual elements OUTSIDE Physics --- (No changes needed here)
interface SimulationViewProps {
  reactorSize: number;
  onStatesUpdate: (states: WorkerParticleState[]) => void; // Pass callback down
}
const SimulationView: React.FC<SimulationViewProps> = ({ reactorSize, onStatesUpdate }) => {
  const { camera } = useThree();
  const floorTexture = useTexture(floorTextureUrl);
  const floorMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const ceilingMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const frontWallMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const backWallMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const leftWallMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const rightWallMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const cameraPosition = useRef(new THREE.Vector3());

  // Get box style and dimensions from store
  const { boxStyle, reactorWidth, reactorHeight, reactorDepth } = useSimulationStore((state) => ({
    boxStyle: state.boxStyle,
    reactorWidth: state.controls.reactorWidth,
    reactorHeight: state.controls.reactorHeight,
    reactorDepth: state.controls.reactorDepth,
  }));

  // Use new dimensions if available, otherwise fall back to reactorSize (backwards compat)
  const width = reactorWidth ?? reactorSize;
  const height = reactorHeight ?? reactorSize;
  const depth = reactorDepth ?? reactorSize;

  useEffect(() => {
    if (floorTexture && floorTexture.image) {
      // ... texture setup ...
    }
  }, [floorTexture]);

  // Wall definitions using rectangular dimensions
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const halfDepth = depth / 2;
  const wallThickness = 0.1;
  const walls = [
    { name: 'floor', ref: floorMatRef, position: new THREE.Vector3(0, -halfHeight, 0), args: [width, wallThickness, depth] as [number, number, number], normal: new THREE.Vector3(0, 1, 0) },
    { name: 'ceiling', ref: ceilingMatRef, position: new THREE.Vector3(0, halfHeight, 0), args: [width, wallThickness, depth] as [number, number, number], normal: new THREE.Vector3(0, -1, 0) },
    { name: 'front', ref: frontWallMatRef, position: new THREE.Vector3(0, 0, halfDepth), args: [width, height, wallThickness] as [number, number, number], normal: new THREE.Vector3(0, 0, -1) },
    { name: 'back', ref: backWallMatRef, position: new THREE.Vector3(0, 0, -halfDepth), args: [width, height, wallThickness] as [number, number, number], normal: new THREE.Vector3(0, 0, 1) },
    { name: 'left', ref: leftWallMatRef, position: new THREE.Vector3(-halfWidth, 0, 0), args: [wallThickness, height, depth] as [number, number, number], normal: new THREE.Vector3(1, 0, 0) },
    { name: 'right', ref: rightWallMatRef, position: new THREE.Vector3(halfWidth, 0, 0), args: [wallThickness, height, depth] as [number, number, number], normal: new THREE.Vector3(-1, 0, 0) },
  ];

  // ... (Wall Transparency Effect Hook - seems fine) ...
  useFrame(() => {
    if (!camera) return;
    cameraPosition.current.copy(camera.position);
    const opaqueOpacity = 0.75;
    const transparentOpacity = 0.05;
    walls.forEach(wall => {
      if (!wall.ref.current) return;
      // ... transparency logic ...
      const wallToCamera = cameraPosition.current.clone().sub(wall.position);
      wall.ref.current.transparent = true;
      wall.ref.current.opacity = (wallToCamera.dot(wall.normal) < 0) ? transparentOpacity : opaqueOpacity;
      wall.ref.current.depthWrite = !(wallToCamera.dot(wall.normal) < 0);
    });
  });

  // ... (Tiling Configuration - seems fine) ...

  return (
    <>
      {/* Pass callback down to SceneContent */}
      <SceneContent
        reactorSize={reactorSize}
        onStatesUpdate={onStatesUpdate}
      />

      {/* --- Visual Walls --- */}
      {boxStyle !== 'hidden' && walls.map(wall => (
        <Box key={wall.name} position={wall.position} args={wall.args} receiveShadow>
          <meshStandardMaterial
            ref={wall.ref as React.RefObject<THREE.MeshStandardMaterial>}
            map={boxStyle === 'normal' ? floorTexture : undefined}
            side={THREE.DoubleSide}
            transparent={boxStyle !== 'normal'}
            opacity={boxStyle === 'glass' ? 0.15 : boxStyle === 'forcefield' ? 0.3 : 1.0}
            color={boxStyle === 'forcefield' ? '#00ffff' : '#ffffff'}
            emissive={boxStyle === 'forcefield' ? '#00ffff' : '#000000'}
            emissiveIntensity={boxStyle === 'forcefield' ? 0 : 0}  // Keep at 0 - forcefield shouldn't bloom
            roughness={boxStyle === 'glass' ? 0.0 : 0.5}
            metalness={boxStyle === 'glass' ? 0.0 : 0.0}
          />
        </Box>
      ))}
    </>
  );
};


// --- Component containing Canvas children (Lights, Effects, Scene) ---
interface SceneSetupAndEffectsProps {
  onStatesUpdate: (states: WorkerParticleState[]) => void;
}

// Manual Bloom implementation using threshold-based selective bloom
// Solution: Limit bright lights (point/directional) to only affect particles (layer 1)
// - Walls only receive ambient light → no bright reflections → won't bloom
// - Particles have emissiveIntensity > threshold → will bloom
const ManualBloomEffect: React.FC = () => {
  const { gl, scene, camera, size } = useThree();
  const composerRef = useRef<ThreeEffectComposer | null>(null);
  const bloomPassRef = useRef<UnrealBloomPass | null>(null);

  // Get bloom parameters from store
  const { bloomIntensity, bloomThreshold, bloomRadius } = useSimulationStore(
    (state) => ({
      bloomIntensity: state.bloomIntensity,
      bloomThreshold: state.bloomThreshold,
      bloomRadius: state.bloomRadius,
    })
  );

  useEffect(() => {
    // Create bloom composer
    const bloomComposer = new ThreeEffectComposer(gl);
    composerRef.current = bloomComposer;

    const renderPass = new RenderPass(scene, camera);
    bloomComposer.addPass(renderPass);

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(size.width, size.height),
      bloomIntensity,
      bloomRadius,
      bloomThreshold
    );
    bloomPassRef.current = bloomPass;
    bloomComposer.addPass(bloomPass);

    bloomComposer.setSize(size.width, size.height);

    return () => {
      if (bloomPassRef.current) {
        bloomPassRef.current.dispose();
      }
      if (composerRef.current) {
        composerRef.current.passes.forEach(pass => {
          if (pass.dispose) pass.dispose();
        });
        composerRef.current.dispose();
      }
    };
  }, [gl, scene, camera, size.width, size.height]);

  // Update bloom parameters when they change
  useEffect(() => {
    if (bloomPassRef.current) {
      bloomPassRef.current.strength = bloomIntensity;
      bloomPassRef.current.threshold = bloomThreshold;
      bloomPassRef.current.radius = bloomRadius;
    }
  }, [bloomIntensity, bloomThreshold, bloomRadius]);

  // Simple render - threshold-based bloom works now because:
  // - Walls only receive ambient light (no bright reflections)
  // - Particles have emissiveIntensity > threshold (1.2 > 1.0)
  // - Walls have emissiveIntensity = 0 < threshold
  useFrame(() => {
    if (composerRef.current) {
      composerRef.current.render();
    }
  }, 1);

  return null;
};

// Component to position camera for screen-aligned view
const CameraPositioner: React.FC = () => {
  const { camera } = useThree();
  const { reactorWidth, reactorHeight, reactorDepth, cameraFov } = useSimulationStore(
    (state) => ({
      reactorWidth: state.controls.reactorWidth,
      reactorHeight: state.controls.reactorHeight,
      reactorDepth: state.controls.reactorDepth,
      cameraFov: state.cameraFov,
    })
  );

  useEffect(() => {
    if (!camera) return;

    const width = reactorWidth ?? 10;
    const height = reactorHeight ?? 10;
    const depth = reactorDepth ?? 10;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const aspect = viewportWidth / viewportHeight;

    // Calculate distance needed to fit reactor height in viewport
    const vFov = (cameraFov * Math.PI) / 180;
    const distanceForHeight = (height / 2) / Math.tan(vFov / 2);

    // Calculate distance needed to fit reactor width in viewport
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
    const distanceForWidth = (width / 2) / Math.tan(hFov / 2);

    // Use the larger distance to ensure both dimensions fit
    // Add depth/2 to position camera outside the front face of the box
    const distance = Math.max(distanceForHeight, distanceForWidth) + (depth / 2);

    // Position camera in front of reactor, looking at origin
    camera.position.set(0, 0, distance);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();

    // console.log('[CameraPositioner] Distance:', distance, 'for reactor:', { width, height, depth });
  }, [camera, reactorWidth, reactorHeight, reactorDepth, cameraFov]);

  return null;
};

// Component to switch between orthographic and perspective camera
const CameraSelector: React.FC = () => {
  const { reactorWidth, reactorHeight, is2DMode } = useSimulationStore(
    (state) => ({
      reactorWidth: state.controls.reactorWidth,
      reactorHeight: state.controls.reactorHeight,
      is2DMode: state.controls.is2DMode,
    })
  );

  const width = reactorWidth ?? 10;
  const height = reactorHeight ?? 10;

  if (is2DMode) {
    // Orthographic camera for 2D mode - no perspective distortion
    return (
      <OrthographicCamera
        makeDefault
        position={[0, 0, 50]}
        left={-width / 2}
        right={width / 2}
        top={height / 2}
        bottom={-height / 2}
        near={0.1}
        far={1000}
      />
    );
  }

  return null; // Use default Canvas perspective camera for 3D mode
};

const SceneSetupAndEffects: React.FC<SceneSetupAndEffectsProps> = ({ onStatesUpdate }) => {
  const { camera } = useThree();
  const {
    backgroundColor,
    ambientLightIntensity,
    reactorSize,
  } = useSimulationStore(
    (state) => ({
      backgroundColor: state.backgroundColor,
      ambientLightIntensity: state.ambientLightIntensity,
      reactorSize: state.controls.reactorSize,
    }), shallow
  );

  // Make camera see all layers
  useEffect(() => {
    if (camera) {
      camera.layers.enableAll();
    }
  }, [camera]);

  // Refs for lights
  const ambientLightRef = useRef<THREE.AmbientLight>(null);
  const pointLightRef = useRef<THREE.PointLight>(null);
  const directionalLightRef = useRef<THREE.DirectionalLight>(null);

  // Configure lights: ambient affects all, point/directional only affect particles (layer 1)
  useEffect(() => {
    if (ambientLightRef.current) ambientLightRef.current.layers.enableAll();
    if (pointLightRef.current) pointLightRef.current.layers.set(1);
    if (directionalLightRef.current) directionalLightRef.current.layers.set(1);
  }, []);

  return (
    <>
      {/* --- Lighting Setup --- */}
      <color attach="background" args={[backgroundColor]} />
      <ambientLight ref={ambientLightRef} intensity={ambientLightIntensity * 1} />
      <pointLight ref={pointLightRef} position={[reactorSize * 2, reactorSize * 2, reactorSize * 2]} intensity={2} />
      <directionalLight
        ref={directionalLightRef}
        position={[reactorSize * 0.5, reactorSize, reactorSize * 0.8]}
        intensity={2.5}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-far={reactorSize * 4}
        shadow-camera-left={-reactorSize * 1.5}
        shadow-camera-right={reactorSize * 1.5}
        shadow-camera-top={reactorSize * 1.5}
        shadow-camera-bottom={-reactorSize * 1.5}
      />

      {/* --- Camera Setup --- */}
      <CameraSelector />
      <CameraPositioner />

      {/* --- Scene Content (particles and walls) --- */}
      <SimulationView reactorSize={reactorSize} onStatesUpdate={onStatesUpdate} />

      {/* --- Post Processing - Manual Bloom using raw Three.js --- */}
      <ManualBloomEffect />

      {/* --- Controls and Stats --- */}
      <OrbitControls target={[0, 0, 0]} enablePan={true} enableZoom={true} enableRotate={true} />
      {/* <Stats className="stats" /> */}
    </>
  );
};

// --- Main Reactor Component (Now just sets up Canvas) ---
interface ReactorProps {
  onStatesUpdate: (states: WorkerParticleState[]) => void;
}

const Reactor: React.FC<ReactorProps> = ({ onStatesUpdate }) => {
  return (
    <div style={{ width: '100%', height: '100%', backgroundColor: '#1a1a1a' }}>
      <Canvas
        shadows
        camera={{ position: [0, 0, 50], fov: 75 }}>
        {/* Pass callback down to SceneSetupAndEffects */}
        <SceneSetupAndEffects onStatesUpdate={onStatesUpdate} />
      </Canvas>
    </div>
  );
};

export default Reactor;

